import { createAction, createReducer, createSelector } from '@reduxjs/toolkit';
import { put, fork, all, select, takeLatest } from 'redux-saga/effects';
import {
  addCompanyHearingRolesToStore,
  addInvitationSourceMappingsToStore,
  addUserHearingRolesToStore,
  deleteInvitationSourceInitialize,
  deleteInvitationSourceMappingsInitialize,
  removeCompanyHearingRolesFromStore,
  removeInvitationSourceMappingsFromStore,
  removeUserHearingRolesFromStore,
  updateHearingInitialize,
  uploadCsvInviteesInitialize,
  uploadExcelInviteesInitialize,
  uploadInvitationGroupInviteesInitialize,
  uploadPersonalInviteesInitialize,
  uploadReviewersInitialize,
} from '../actions/api';
import {
  hearingStatusByIdSelector,
  hearingStatusByStatusSelector,
  allHearingStatusSelector,
} from '../apiFeatures/hearingStatusSlice';
import { awaitApiCall } from '../helpers/sagaHelpers';
import { getAttr, getEntityId, getRelId } from '../helpers/apiHelpers';
import {
  COMPANYHEARINGROLE_INCLUDE,
  HEARINGROLE_INVITEE,
  HEARINGROLE_REVIEWER,
  HEARINGROLE_TYPE,
  HEARINGSTATUS_CREATED,
  HEARINGSTATUS_DRAFT,
  HEARING_TYPE,
  INVITATIONSOURCEMAPPING_INCLUDE,
  INVITATIONSOURCETYPE_CSV,
  INVITATIONSOURCETYPE_EXCEL,
  USERHEARINGROLE_INCLUDE,
  USERHEARINGROLE_TYPE,
  USER_TYPE,
} from '../../constants/api';
import { parseApiError } from '../globalMessageSlice';
import { hearingRoleByRoleSelector } from '../apiFeatures/hearingRoleSlice';
import { allUserHearingRolesWithRoleByHearingIdSelector } from '../apiFeatures/userHearingRoleSlice';
import {
  getIncludedData,
  getRequestMetaData,
} from '../helpers/selectorHelpers';
import { allCompanyHearingRolesWithRoleByHearingIdSelector } from '../apiFeatures/companyHearingRoleSlice';
import { allInvitationSourceMappingsByUserOrCompanyHearingRoleIds } from '../apiFeatures/invitationSourceMappingSlice';

// -------- Actions ---------
const updateHearingModelAttribute = createAction(
  'page/createHearing/updateAttributes',
);
const updateHearingModelRelationship = createAction(
  'page/createhearing/updateRelationships',
);
const setHearingModel = createAction('page/createHearing/setHearingModel');
const updateHearingMetaData = createAction('page/createHearing/updateHearing');
const setIsUpdatingHearing = createAction(
  'page/createHearing/setIsUpdatingHearing',
);
const setSelectedEsdhTemplateId = createAction(
  'page/createhearing/setSelectedEsdhTemplateId',
);
const setShowErrors = createAction('page/createHearing/setShowErrors');
const setPageStep = createAction('page/createHearing/setStep');
const initializeState = createAction('page/createHearing/initializeState');
const uploadReviewers = createAction('page/createHearing/uploadReviewers');
const uploadFileInvitees = createAction(
  'page/createHearing/uploadFileInvitees',
);
const uploadPersonalInvitees = createAction(
  'page/createHearing/uploadPersonalInvitees',
);
const uploadInvitationGroupInvitees = createAction(
  'page/createHearing/uploadInvitationGroupInvitees',
);
const deleteInvitationSource = createAction(
  'page/createHearing/deleteInvitationSource',
);
const deleteInvitationSourceMappings = createAction(
  'page/createHearing/deleteInvitationSourceMappings',
);
const setIsUploadingReviewers = createAction(
  'page/createHearing/setIsUploadingReviewers',
);
const setIsUploadingFileInvitees = createAction(
  'page/createHearing/setIsUploadingFileInvitees',
);
const setIsUploadingPersonalInvitees = createAction(
  'page/createHearing/setIsUploadingPersonalInvitees',
);
const setIsUploadingInvitationGroupInvitees = createAction(
  'page/createHearing/setIsUploadingInvitationGroupInvitees',
);
const setIsDeletingInvitationSource = createAction(
  'page/createHearing/setIsDeletingInvitationSource',
);
const setIsDeletingInvitationSourceMappings = createAction(
  'page/createHearing/setIsDeletingInvitationSourceMappings',
);
const setIsReviewerPanelOpen = createAction(
  'page/createHearing/setIsReviewerPanelOpen',
);
const setIsFileInviteePanelOpen = createAction(
  'page/createHearing/setIsFileInviteePanelOpen',
);
const setIsPersonalInviteePanelOpen = createAction(
  'page/createHearing/setIsPersonalInviteePanelOpen',
);
const setIsInvitationGroupInviteePanelOpen = createAction(
  'page/createHearing/setIsInvitationGroupInviteePanelOpen',
);
const setIsDeleteInvitationSourcePanelOpen = createAction(
  'page/createHearing/setIsDeleteInvitationSourcePanelOpen',
);
const setIsDeleteInvitationSourceMappingsPanelOpen = createAction(
  'page/createHearing/setIsDeleteInvitationSourceMappingsPanelOpen',
);
const setInvitationSourceMappingsToDelete = createAction(
  'page/createHearing/setInvitationSourceMappingsToDelete',
);
const setDeleteFromAllInvitationSources = createAction(
  'page/createHearing/setDeleteFromAllInvitationSources',
);
const setIsInviteeChangedPanelOpen = createAction(
  'page/createHearing/setIsInviteeChangedPanelOpen',
);
const setInviteeChangedMetaData = createAction(
  'page/createHearing/setInviteeChangedMetaData',
);

const initialState = {
  hearingModel: null,
  isUpdatingHearing: false,
  selectedEsdhTemplateId: null,
  showErrors: false,
  pageStep: 1,
  isUploadingReviewers: false,
  isUploadingFileInvitees: false,
  isUploadingPersonalInvitees: false,
  isUploadingInvitationGroupInvitees: false,
  isDeletingInvitationSource: false,
  isDeletingInvitationSourceMappings: false,
  isReviewerPanelOpen: false,
  isFileInviteePanelOpen: false,
  isPersonalInviteePanelOpen: false,
  isInvitationGroupInviteePanelOpen: false,
  isDeleteInvitationSourcePanelOpen: false,
  isDeleteInvitationSourceMappingsPanelOpen: false,
  invitationSourceMappingsToDelete: [],
  deleteFromAllInvitationSources: false,
  isInviteeChangedPanelOpen: false,
  inviteeChangedMetaData: {},
};
// -------- Reducers ---------
const createHearingPageReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(updateHearingModelAttribute, (state, action) => {
      const { attribute, value } = action.payload;
      const newModel = Object.assign({}, state.hearingModel, {
        attributes: {
          ...state.hearingModel.attributes,
          [attribute]: value,
        },
      });
      state.hearingModel = newModel;
    })
    .addCase(updateHearingModelRelationship, (state, action) => {
      const { relationship, id } = action.payload;
      const newModel = Object.assign({}, state.hearingModel, {
        relationships: {
          ...state.hearingModel.relationships,
          [relationship]: id
            ? {
                data: {
                  id: id,
                  type: relationship.toLowerCase(),
                },
              }
            : undefined,
        },
      });
      state.hearingModel = newModel;
    })
    .addCase(setHearingModel, (state, action) => {
      state.hearingModel = action.payload;
    })
    .addCase(setIsUpdatingHearing, (state, action) => {
      state.isUpdatingHearing = action.payload;
    })
    .addCase(setSelectedEsdhTemplateId, (state, action) => {
      state.selectedEsdhTemplateId = action.payload;
    })
    .addCase(setShowErrors, (state, action) => {
      state.showErrors = action.payload;
    })
    .addCase(setPageStep, (state, action) => {
      window.scrollTo(0, 0);
      state.pageStep = action.payload;
    })
    .addCase(setIsUploadingReviewers, (state, action) => {
      state.isUploadingReviewers = action.payload;
    })
    .addCase(setIsUploadingFileInvitees, (state, action) => {
      state.isUploadingFileInvitees = action.payload;
    })
    .addCase(setIsUploadingPersonalInvitees, (state, action) => {
      state.isUploadingPersonalInvitees = action.payload;
    })
    .addCase(setIsUploadingInvitationGroupInvitees, (state, action) => {
      state.isUploadingInvitationGroupInvitees = action.payload;
    })
    .addCase(setIsDeletingInvitationSource, (state, action) => {
      state.isDeletingInvitationSource = action.payload;
    })
    .addCase(setIsDeletingInvitationSourceMappings, (state, action) => {
      state.isDeletingInvitationSourceMappings = action.payload;
    })
    .addCase(setIsReviewerPanelOpen, (state, action) => {
      state.isReviewerPanelOpen = action.payload;
    })
    .addCase(setIsFileInviteePanelOpen, (state, action) => {
      state.isFileInviteePanelOpen = action.payload;
    })
    .addCase(setIsPersonalInviteePanelOpen, (state, action) => {
      state.isPersonalInviteePanelOpen = action.payload;
    })
    .addCase(setIsInvitationGroupInviteePanelOpen, (state, action) => {
      state.isInvitationGroupInviteePanelOpen = action.payload;
    })
    .addCase(setIsDeleteInvitationSourcePanelOpen, (state, action) => {
      state.isDeleteInvitationSourcePanelOpen = action.payload;
    })
    .addCase(setIsDeleteInvitationSourceMappingsPanelOpen, (state, action) => {
      state.isDeleteInvitationSourceMappingsPanelOpen = action.payload;
    })
    .addCase(setInvitationSourceMappingsToDelete, (state, action) => {
      state.invitationSourceMappingsToDelete = action.payload;
    })
    .addCase(setDeleteFromAllInvitationSources, (state, action) => {
      state.deleteFromAllInvitationSources = action.payload;
    })
    .addCase(setIsInviteeChangedPanelOpen, (state, action) => {
      state.isInviteeChangedPanelOpen = action.payload;
    })
    .addCase(setInviteeChangedMetaData, (state, action) => {
      state.inviteeChangedMetaData = action.payload;
    })
    .addCase(initializeState, () => initialState);
});

// -------- Sagas ---------
export function* createHearingPageSaga() {
  yield all([
    fork(updateHearingMetaDataSaga),
    fork(uploadReviewersSaga),
    fork(uploadFileInviteesSaga),
    fork(uploadPersonalInviteesSaga),
    fork(uploadInvitationGroupInviteesSaga),
    fork(deleteInvitationSourceSaga),
    fork(deleteInvitationSourceMappingsSaga),
  ]);
}

function* uploadFileInviteesSaga() {
  yield takeLatest(uploadFileInvitees.type, function* (action) {
    const { hearingId, invitationSource, file } = action.payload;

    if (!hearingId || !file) {
      return;
    }

    yield put(setIsUploadingFileInvitees(true));

    const invitationSourceType = getAttr(
      invitationSource,
      'invitationSourceType',
    );
    const invitationSourceId = getEntityId(invitationSource);

    let initAction;
    let actionResult;

    if (invitationSourceType === INVITATIONSOURCETYPE_CSV) {
      initAction = yield put(
        uploadCsvInviteesInitialize({
          dto: { formFile: file },
          hearingId,
          invitationSourceId,
        }),
      );
      actionResult = yield awaitApiCall(initAction);
    } else if (invitationSourceType === INVITATIONSOURCETYPE_EXCEL) {
      initAction = yield put(
        uploadExcelInviteesInitialize({
          dto: { formFile: file },
          hearingId,
          invitationSourceId,
        }),
      );
      actionResult = yield awaitApiCall(initAction);
    }

    yield* handleInviteesResult(
      actionResult,
      hearingId,
      setIsUploadingFileInvitees,
      setIsFileInviteePanelOpen,
    );
  });
}

function* uploadPersonalInviteesSaga() {
  yield takeLatest(uploadPersonalInvitees.type, function* (action) {
    const {
      hearingId,
      invitationSourceId,
      inviteeIdentifiers,
    } = action.payload;

    if (!hearingId || !invitationSourceId || !inviteeIdentifiers) {
      return;
    }

    yield put(setIsUploadingPersonalInvitees(true));

    const dtos = inviteeIdentifiers.map((inviteeIdentifier) => ({
      data: { ...inviteeIdentifier },
    }));

    const initAction = yield put(
      uploadPersonalInviteesInitialize({
        dtos,
        hearingId,
        invitationSourceId,
      }),
    );

    const actionResult = yield awaitApiCall(initAction);

    yield* handleInviteesResult(
      actionResult,
      hearingId,
      setIsUploadingPersonalInvitees,
      setIsPersonalInviteePanelOpen,
    );
  });
}

function* uploadInvitationGroupInviteesSaga() {
  yield takeLatest(uploadInvitationGroupInvitees.type, function* (action) {
    const { hearingId, invitationSourceId, invitationGroupId } = action.payload;

    if (!hearingId || !invitationSourceId || !invitationGroupId) {
      return;
    }

    yield put(setIsUploadingInvitationGroupInvitees(true));

    const initAction = yield put(
      uploadInvitationGroupInviteesInitialize({
        hearingId,
        invitationSourceId,
        invitationGroupId,
      }),
    );

    const actionResult = yield awaitApiCall(initAction);

    yield* handleInviteesResult(
      actionResult,
      hearingId,
      setIsUploadingInvitationGroupInvitees,
      setIsInvitationGroupInviteePanelOpen,
    );
  });
}

function* handleInviteesResult(
  actionResult,
  hearingId,
  setLoadingAction,
  setPanelOpenAction,
) {
  if (actionResult.success) {
    const resultData = getIncludedData(actionResult.success);
    const metaData = getRequestMetaData(actionResult.success);
    const newUserHearingRoles = resultData.filter(
      (include) => include.type === USERHEARINGROLE_INCLUDE,
    );
    const newCompanyHearingRoles = resultData.filter(
      (include) => include.type === COMPANYHEARINGROLE_INCLUDE,
    );
    const newInvitationSourceMappings = resultData.filter(
      (include) => include.type === INVITATIONSOURCEMAPPING_INCLUDE,
    );

    const existingUserHearingRoleIds = (yield select(
      allUserHearingRolesWithRoleByHearingIdSelector(
        HEARINGROLE_INVITEE,
        hearingId,
      ),
    )).map((uhr) => getEntityId(uhr));
    const existingCompanyHearingRoleIds = (yield select(
      allCompanyHearingRolesWithRoleByHearingIdSelector(
        HEARINGROLE_INVITEE,
        hearingId,
      ),
    )).map((chr) => getEntityId(chr));
    const existingInvitationSourceMappingsIds = (yield select(
      allInvitationSourceMappingsByUserOrCompanyHearingRoleIds(
        existingUserHearingRoleIds,
        existingCompanyHearingRoleIds,
      ),
    )).map((ism) => getEntityId(ism));

    yield put(removeUserHearingRolesFromStore(existingUserHearingRoleIds));
    yield put(
      removeCompanyHearingRolesFromStore(existingCompanyHearingRoleIds),
    );
    yield put(
      removeInvitationSourceMappingsFromStore(
        existingInvitationSourceMappingsIds,
      ),
    );
    yield put(addUserHearingRolesToStore(newUserHearingRoles));
    yield put(addCompanyHearingRolesToStore(newCompanyHearingRoles));
    yield put(addInvitationSourceMappingsToStore(newInvitationSourceMappings));

    yield put(
      setInviteeChangedMetaData({
        deletedInvitees: metaData.DeletedInvitees,
        existingInvitees: metaData.ExistingInvitees,
        inviteesNotDeleted: metaData.InviteesNotDeleted,
        newInvitees: metaData.NewInvitees,
      }),
    );

    yield put(setIsInviteeChangedPanelOpen(true));
  } else {
    yield put(parseApiError(actionResult.error.payload));
  }

  yield put(setPanelOpenAction(false));
  yield put(setLoadingAction(false));
}

function* uploadReviewersSaga() {
  yield takeLatest(uploadReviewers.type, function* (action) {
    const { hearingId, userIds } = action.payload;

    if (!hearingId || !userIds) {
      return;
    }

    yield put(setIsUploadingReviewers(true));

    const hearingRole = yield select(
      hearingRoleByRoleSelector(HEARINGROLE_REVIEWER),
    );
    const hearingRoleId = getEntityId(hearingRole);

    const dto = userIds.map((userId) => ({
      data: {
        type: USERHEARINGROLE_TYPE,
        attributes: {},
        relationships: {
          hearing: {
            data: {
              id: hearingId,
              type: HEARING_TYPE,
            },
          },
          user: {
            data: {
              id: userId,
              type: USER_TYPE,
            },
          },
          hearingRole: {
            data: {
              id: hearingRoleId,
              type: HEARINGROLE_TYPE,
            },
          },
        },
      },
    }));

    const currentUserHearingRolesToDelete = yield select(
      allUserHearingRolesWithRoleByHearingIdSelector(
        HEARINGROLE_REVIEWER,
        hearingId,
      ),
    );
    const userHearingRoleIdsToDelete = currentUserHearingRolesToDelete.map(
      (userHearingRole) => getEntityId(userHearingRole),
    );

    const uploadReviewersInitAction = yield put(
      uploadReviewersInitialize({ hearingId, models: dto }),
    );
    const uploadReviewersResult = yield awaitApiCall(uploadReviewersInitAction);

    if (uploadReviewersResult.success) {
      yield put(removeUserHearingRolesFromStore(userHearingRoleIdsToDelete));
      yield put(setIsReviewerPanelOpen(false));
    } else {
      yield put(parseApiError(uploadReviewersResult.error.payload));
    }
    yield put(setIsUploadingReviewers(false));
  });
}

function* updateHearingMetaDataSaga() {
  yield takeLatest(updateHearingMetaData.type, function* () {
    let hearingModel = yield select(hearingModelSelector);
    const oldHearingModel = yield select(hearingModelSelector);
    const hearingStatus = yield select(
      hearingStatusByIdSelector(getRelId(hearingModel, 'hearingStatus')),
    );

    // If hearing is updated from the created-status, it should change status to draft
    if (getAttr(hearingStatus, 'status') === HEARINGSTATUS_CREATED) {
      var hearingStatusDraft = yield select(
        hearingStatusByStatusSelector(HEARINGSTATUS_DRAFT),
      );
      yield put(
        updateHearingModelRelationship({
          relationship: 'hearingStatus',
          id: getEntityId(hearingStatusDraft),
        }),
      );
      hearingModel = yield select(hearingModelSelector);
    }

    const updateHearingAction = yield put(
      updateHearingInitialize({
        changes: hearingModel,
      }),
    );
    yield put(setIsUpdatingHearing(true));

    const updateHearingActionResult = yield awaitApiCall(updateHearingAction);

    if (updateHearingActionResult.success) {
      yield put(setSelectedEsdhTemplateId(null));
      yield put(setShowErrors(false));
      yield put(setPageStep(2));
    } else if (updateHearingActionResult.error) {
      yield put(parseApiError(updateHearingActionResult.error.payload));
      yield put(setHearingModel(oldHearingModel));
      yield put(setShowErrors(true));
    }
    yield put(setIsUpdatingHearing(false));
  });
}

function* deleteInvitationSourceSaga() {
  yield takeLatest(deleteInvitationSource.type, function* (action) {
    const { hearingId, invitationSourceName } = action.payload;

    if (!hearingId || !invitationSourceName) {
      return;
    }

    yield put(setIsDeletingInvitationSource(true));

    const initAction = yield put(
      deleteInvitationSourceInitialize({ hearingId, invitationSourceName }),
    );

    const actionResult = yield awaitApiCall(initAction);

    yield* handleInviteesResult(
      actionResult,
      hearingId,
      setIsDeletingInvitationSource,
      setIsDeleteInvitationSourcePanelOpen,
    );
  });
}

function* deleteInvitationSourceMappingsSaga() {
  yield takeLatest(deleteInvitationSourceMappings.type, function* (action) {
    const {
      hearingId,
      invitationSourceMappingIds,
      deleteFromAllInvitationSources,
    } = action.payload;

    if (!hearingId || !invitationSourceMappingIds) {
      return;
    }

    yield put(setIsDeletingInvitationSourceMappings(true));

    const initAction = yield put(
      deleteInvitationSourceMappingsInitialize({
        hearingId,
        invitationSourceMappingIds,
        deleteFromAllInvitationSources,
      }),
    );

    const actionResult = yield awaitApiCall(initAction);

    yield* handleInviteesResult(
      actionResult,
      hearingId,
      setIsDeletingInvitationSourceMappings,
      setIsDeleteInvitationSourceMappingsPanelOpen,
    );
  });
}

// -------- Selectors ---------
const selectCreateHearingState = (state) => state.page.createHearing;

const hearingModelSelector = createSelector(
  selectCreateHearingState,
  (state) => state.hearingModel,
);
const isUpdatingHearingSelector = createSelector(
  selectCreateHearingState,
  (state) => state.isUpdatingHearing,
);
const isHearingModelInCreatedStatusSelector = createSelector(
  [hearingModelSelector, allHearingStatusSelector],
  (hearingModel, hearingStatuses) => {
    const status = hearingStatuses.find(
      (s) => getEntityId(s) === getRelId(hearingModel, 'hearingStatus'),
    );
    return getAttr(status, 'status') === HEARINGSTATUS_CREATED;
  },
);
const selectedEsdhTemplateIdSelector = createSelector(
  selectCreateHearingState,
  (state) => state.selectedEsdhTemplateId,
);
const showErrorsSelector = createSelector(
  selectCreateHearingState,
  (state) => state.showErrors,
);
const pageStepSelector = createSelector(
  selectCreateHearingState,
  (state) => state.pageStep,
);
const isUploadingReviewersSelector = createSelector(
  selectCreateHearingState,
  (state) => state.isUploadingReviewers,
);
const isUploadingFileInviteesSelector = createSelector(
  selectCreateHearingState,
  (state) => state.isUploadingFileInvitees,
);
const isUploadingPersonalInviteesSelector = createSelector(
  selectCreateHearingState,
  (state) => state.isUploadingPersonalInvitees,
);
const isDeletingInvitationSourceSelector = createSelector(
  selectCreateHearingState,
  (state) => state.isDeletingInvitationSource,
);
const isDeletingInvitationSourceMappingsSelector = createSelector(
  selectCreateHearingState,
  (state) => state.isDeletingInvitationSourceMappings,
);
const isUploadingInvitationGroupInviteesSelector = createSelector(
  selectCreateHearingState,
  (state) => state.isUploadingInvitationGroupInvitees,
);
const isReviewerPanelOpenSelector = createSelector(
  selectCreateHearingState,
  (state) => state.isReviewerPanelOpen,
);
const isFileInviteePanelOpenSelector = createSelector(
  selectCreateHearingState,
  (state) => state.isFileInviteePanelOpen,
);
const isPersonalInviteePanelOpenSelector = createSelector(
  selectCreateHearingState,
  (state) => state.isPersonalInviteePanelOpen,
);
const isInvitationGroupInviteePanelOpenSelector = createSelector(
  selectCreateHearingState,
  (state) => state.isInvitationGroupInviteePanelOpen,
);
const isDeleteInvitationSourcePanelOpenSelector = createSelector(
  selectCreateHearingState,
  (state) => state.isDeleteInvitationSourcePanelOpen,
);
const isDeleteInvitationSourceMappingsPanelOpenSelector = createSelector(
  selectCreateHearingState,
  (state) => state.isDeleteInvitationSourceMappingsPanelOpen,
);
const invitationSourceMappingsToDeleteSelector = createSelector(
  selectCreateHearingState,
  (state) => state.invitationSourceMappingsToDelete,
);
const deleteFromAllInvitationSourcesSelector = createSelector(
  selectCreateHearingState,
  (state) => state.deleteFromAllInvitationSources,
);
const isInviteeChangedPanelOpenSelector = createSelector(
  selectCreateHearingState,
  (state) => state.isInviteeChangedPanelOpen,
);
const inviteeChangedMetaDataSelector = createSelector(
  selectCreateHearingState,
  (state) => state.inviteeChangedMetaData,
);

// -------- Exporting ---------
export {
  setHearingModel,
  updateHearingModelAttribute,
  updateHearingModelRelationship,
  hearingModelSelector,
  updateHearingMetaData,
  isUpdatingHearingSelector,
  isHearingModelInCreatedStatusSelector,
  setSelectedEsdhTemplateId,
  selectedEsdhTemplateIdSelector,
  showErrorsSelector,
  pageStepSelector,
  initializeState,
  setPageStep,
  uploadReviewers,
  uploadFileInvitees,
  uploadPersonalInvitees,
  deleteInvitationSource,
  deleteInvitationSourceMappings,
  uploadInvitationGroupInvitees,
  isUploadingReviewersSelector,
  isUploadingFileInviteesSelector,
  isUploadingPersonalInviteesSelector,
  isUploadingInvitationGroupInviteesSelector,
  isDeletingInvitationSourceSelector,
  isDeletingInvitationSourceMappingsSelector,
  setIsReviewerPanelOpen,
  setIsFileInviteePanelOpen,
  setIsPersonalInviteePanelOpen,
  setIsInvitationGroupInviteePanelOpen,
  setIsDeleteInvitationSourcePanelOpen,
  setIsDeleteInvitationSourceMappingsPanelOpen,
  setInvitationSourceMappingsToDelete,
  setDeleteFromAllInvitationSources,
  isReviewerPanelOpenSelector,
  isFileInviteePanelOpenSelector,
  isPersonalInviteePanelOpenSelector,
  isInvitationGroupInviteePanelOpenSelector,
  isDeleteInvitationSourcePanelOpenSelector,
  isDeleteInvitationSourceMappingsPanelOpenSelector,
  invitationSourceMappingsToDeleteSelector,
  deleteFromAllInvitationSourcesSelector,
  setIsInviteeChangedPanelOpen,
  isInviteeChangedPanelOpenSelector,
  setInviteeChangedMetaData,
  inviteeChangedMetaDataSelector,
};
export default createHearingPageReducer;
